// Agendas, packets and minutes past the retired Board app (ADR 0031):
// agenda item types and sub-items, templates and copy-forward, the consent
// agenda, items Hub adds on its own, timers, action-item templates, the
// flexible minutes, packet reminders, the President's signature and the
// compiled board book.
//
// The pure rules here (agenda time, timers, reminders, the consent motion's
// text, minutes edit rules) are used by Platform Core to decide and by the
// staff UI to explain. Core is the authority.

import { z } from 'zod'
import { daysBetween } from './finance-hub'
import type { MeetingBody, MeetingKind, PersonRef } from './governance'
import type { MinutesSection } from './governance-decisions'
import { isFinancialReportTitle } from './governance-resolutions'
import type { SlateReport } from './governance-nominations'
import type { BoardBookManual } from './governance-manuals'

// --------------------------------------------------------- item types

// The Board app's set where it meant something (heading, item, consent,
// old and new business, appointment, resignation, financial report, the
// elections, adjourn, committee report, email votes, Executive Committee
// report), plus report, discussion, action, executive session and open
// action items. 'item' is a general item (everything made before 0050).
export const AGENDA_ITEM_TYPES = [
  'item',
  'heading',
  'report',
  'discussion',
  'action',
  'consent_agenda',
  'old_business',
  'new_business',
  'appointment',
  'resignation',
  'financial_report',
  'executive_session',
  'elections',
  'committee_report',
  'email_meeting_report',
  'executive_committee_report',
  'action_items_report',
  'adjourn',
] as const
export type AgendaItemType = (typeof AGENDA_ITEM_TYPES)[number]
export const AGENDA_ITEM_TYPE_LABELS: Record<AgendaItemType, string> = {
  item: 'Item',
  heading: 'Heading',
  report: 'Report',
  discussion: 'Discussion',
  action: 'Action',
  consent_agenda: 'Consent agenda',
  old_business: 'Old business',
  new_business: 'New business',
  appointment: 'Appointment',
  resignation: 'Resignation',
  financial_report: 'Financial report',
  executive_session: 'Executive session',
  elections: 'Elections',
  committee_report: 'Committee report',
  email_meeting_report: 'Email meetings to record',
  executive_committee_report: 'Executive Committee actions',
  action_items_report: 'Open action items',
  adjourn: 'Adjourn',
}
// The groups: always on the top level. The consent agenda holds one level
// of sub-items; any other item may hold sub-items three levels deep
// (ADR 0062).
export const AGENDA_GROUP_TYPES: AgendaItemType[] = ['consent_agenda', 'old_business', 'new_business']
export const isAgendaGroup = (t: AgendaItemType | string) => (AGENDA_GROUP_TYPES as string[]).includes(t)

// ---------------------------------------------------------------- nesting

// Sub-items under any item (owner, 2026-10-26; ADR 0062), up to three
// levels below the top, numbered 4, 4a, 4a-i, 4a-i-1. The consent agenda,
// old business and new business stay on the top level; the consent agenda
// holds one level (each item is part of its single motion, and nothing from
// the executive session); an item under an executive-session item is in the
// executive session too. The database holds the same rules (core migration
// agenda_item_nesting triggers).
export const AGENDA_MAX_DEPTH = 3

const letters = (n: number): string => {
  let out = ''
  for (let k = n; k > 0; k = Math.floor((k - 1) / 26)) out = String.fromCharCode(97 + ((k - 1) % 26)) + out
  return out
}
const ROMAN: [number, string][] = [
  [1000, 'm'],
  [900, 'cm'],
  [500, 'd'],
  [400, 'cd'],
  [100, 'c'],
  [90, 'xc'],
  [50, 'l'],
  [40, 'xl'],
  [10, 'x'],
  [9, 'ix'],
  [5, 'v'],
  [4, 'iv'],
  [1, 'i'],
]
const roman = (n: number): string => {
  let out = ''
  let k = n
  for (const [v, s] of ROMAN)
    while (k >= v) {
      out += s
      k -= v
    }
  return out
}

// An item's number from its place at each level (1-based): [4] → "4",
// [4, 1] → "4a", [4, 1, 1] → "4a-i", [4, 1, 1, 1] → "4a-i-1".
export function agendaNumber(path: number[]): string {
  const [top, ...rest] = path
  let out = `${top ?? ''}`
  rest.forEach((n, k) => (out += k === 0 ? letters(n) : k === 1 ? `-${roman(n)}` : `-${n}`))
  return out
}

type TreeNode = { id: string; parentId?: string | null; parent_id?: string | null }
const parentOfNode = (i: TreeNode) => i.parentId ?? i.parent_id ?? null

export type AgendaOutlineEntry<T> = { item: T; depth: number; number: string; parentId: string | null }

// The agenda in reading order, each item followed by its sub-items, with
// its depth (0 on the top level) and number. Siblings keep the order they
// are given in; an item whose parent isn't in the list reads on the top
// level.
export function agendaOutline<T extends TreeNode>(items: T[]): AgendaOutlineEntry<T>[] {
  const ids = new Set(items.map((i) => i.id))
  const kids = new Map<string | null, T[]>()
  for (const i of items) {
    const p = parentOfNode(i)
    const key = p && ids.has(p) && p !== i.id ? p : null
    kids.set(key, [...(kids.get(key) ?? []), i])
  }
  const out: AgendaOutlineEntry<T>[] = []
  const seen = new Set<string>()
  const walk = (parent: string | null, path: number[]) => {
    ;(kids.get(parent) ?? []).forEach((item, k) => {
      if (seen.has(item.id)) return
      seen.add(item.id)
      const at = [...path, k + 1]
      out.push({ item, depth: path.length, number: agendaNumber(at), parentId: parent })
      walk(item.id, at)
    })
  }
  walk(null, [])
  // A cycle (never written, but never trusted either) reads on the top level.
  for (const item of items) if (!seen.has(item.id)) out.push({ item, depth: 0, number: '', parentId: null })
  return out
}

// Each item's number, by id.
export function agendaNumbers(items: TreeNode[]): Record<string, string> {
  return Object.fromEntries(agendaOutline(items).map((e) => [e.item.id, e.number]))
}

// The ids under an item, at any depth.
export function agendaDescendants(items: TreeNode[], id: string): string[] {
  const out: string[] = []
  const walk = (p: string) => {
    for (const c of items.filter((x) => parentOfNode(x) === p && x.id !== p)) {
      if (out.includes(c.id) || c.id === id) continue
      out.push(c.id)
      walk(c.id)
    }
  }
  walk(id)
  return out
}

export type NestingNode = { id: string; parentId: string | null; itemType: string; executive: boolean }

// What is wrong with an agenda's shape, in words, or null. Checked on the
// whole tree an edit would leave.
export function agendaNestingProblem(items: NestingNode[]): string | null {
  const byId = new Map(items.map((i) => [i.id, i]))
  for (const i of items) {
    if (!i.parentId) continue
    const parent = byId.get(i.parentId)
    if (!parent) return 'That item is not on this agenda'
    if (isAgendaGroup(i.itemType)) return 'The consent agenda, old business and new business stay on the top level'
    let depth = 1
    const seen = new Set([i.id])
    for (let p: NestingNode | undefined = parent; p?.parentId; p = byId.get(p.parentId)) {
      if (seen.has(p.id)) return 'An item cannot go under its own sub-item'
      seen.add(p.id)
      depth += 1
    }
    if (depth > AGENDA_MAX_DEPTH) return `Sub-items go ${AGENDA_MAX_DEPTH} levels deep at most`
    const grand = parent.parentId ? byId.get(parent.parentId) : undefined
    if (grand?.itemType === 'consent_agenda') return 'Consent agenda items have no sub-items'
    if (parent.itemType === 'consent_agenda' && i.executive) return 'Nothing from the executive session goes on the consent agenda'
    if (parent.executive && !i.executive) return 'An item under an executive-session item is in the executive session too'
  }
  return null
}

// The minutes an item is planned to take: its own, or its sub-items' sum.
type Timed = { id: string; parentId: string | null; durationMinutes: number | null }
function plannedMinutes(items: Timed[], id: string, own: number | null, seen: Set<string>): number {
  if (own) return own
  if (seen.has(id)) return 0
  seen.add(id)
  return items.filter((c) => c.parentId === id).reduce((n, c) => n + plannedMinutes(items, c.id, c.durationMinutes, seen), 0)
}
export function agendaItemMinutes(items: Timed[], id: string): number {
  return plannedMinutes(items, id, items.find((i) => i.id === id)?.durationMinutes ?? null, new Set())
}

// A reader without the executive session sees an executive-session item as
// its place only: what is under it comes out, and its time covers theirs.
export function collapseRedacted<T extends { id: string; parentId?: string | null; redacted?: boolean; durationMinutes: number | null }>(items: T[]): T[] {
  const hidden = new Set(items.filter((i) => i.redacted).flatMap((i) => agendaDescendants(items, i.id)))
  if (hidden.size === 0) return items
  const timed = items.map((i) => ({ id: i.id, parentId: i.parentId ?? null, durationMinutes: i.durationMinutes }))
  return items.filter((i) => !hidden.has(i.id)).map((i) => (i.redacted && !i.durationMinutes && agendaDescendants(items, i.id).length > 0 ? { ...i, durationMinutes: agendaItemMinutes(timed, i.id) || null } : i))
}

// Items Hub adds on its own to a new regular board meeting. 'agenda_approval'
// (owner, 2026-10-26): "Approval of the agenda" on the consent agenda of
// every regular and annual board meeting, unless the practice setting is off.
export const AUTO_SOURCES = ['prior_minutes', 'tabled_motion', 'postponed_motion', 'email_meetings', 'executive_committee', 'committee_report', 'action_items', 'consent_group', 'old_business_group', 'agenda_approval'] as const
export type AutoSource = (typeof AUTO_SOURCES)[number]
export const AUTO_SOURCE_LABELS: Record<AutoSource, string> = {
  prior_minutes: 'Added by Hub: minutes awaiting approval',
  tabled_motion: 'Added by Hub: laid on the table at the last meeting',
  postponed_motion: 'Added by Hub: postponed to this meeting',
  email_meetings: 'Added by Hub: email meetings to record',
  executive_committee: 'Added by Hub: Executive Committee actions to report',
  committee_report: 'Added by Hub: the committee met since the last board meeting',
  action_items: 'Added by Hub: open action items',
  consent_group: 'Added by Hub',
  old_business_group: 'Added by Hub',
  agenda_approval: 'Added by Hub: approval of the agenda',
}
export const AGENDA_APPROVAL_TITLE = 'Approval of the agenda'
// The Nominating Committee's slate (ADR 0060) rides on the committee-report
// source with its own references: 'slate:<election>' on the May regular
// meeting (received, no vote) and 'election:<election>' on the Annual
// Meeting ("Elect the slate").
export const SLATE_REPORT_REF_PREFIX = 'slate:'
export const ELECTION_ITEM_REF_PREFIX = 'election:'
// ADR 0067: an Executive Committee action awaiting the board's
// ratification rides on the Executive Committee's source as
// 'ratify:<resolution>', its own item with a proposed motion.
export const RATIFY_ITEM_REF_PREFIX = 'ratify:'
export const ratifyItemTitle = (number: string) => `Ratify Executive Committee action ${number}`
export const ratifyMotionTitle = (number: string) => `Ratify ${number}`
// ADR 0069: an Executive Committee recommendation to the board rides on the
// same source as 'recommend:<resolution>', its own item with the proposed
// board motion.
export const RECOMMEND_ITEM_REF_PREFIX = 'recommend:'
export const recommendItemTitle = (number: string) => `Executive Committee recommendation ${number}`
export const EXECUTIVE_COMMITTEE_NAME = 'Executive Committee'
export function autoSourceLabel(source: AutoSource, ref: string | null | undefined): string {
  if (source === 'executive_committee' && ref?.startsWith(RATIFY_ITEM_REF_PREFIX)) return 'Added by Hub: Executive Committee action to ratify'
  if (source === 'executive_committee' && ref?.startsWith(RECOMMEND_ITEM_REF_PREFIX)) return 'Added by Hub: Executive Committee recommendation'
  if (source === 'committee_report' && ref?.startsWith(SLATE_REPORT_REF_PREFIX)) return 'Added by Hub: the Nominating Committee presents its slate'
  if (source === 'committee_report' && ref?.startsWith(ELECTION_ITEM_REF_PREFIX)) return 'Added by Hub: the annual election'
  if ((source === 'tabled_motion' || source === 'postponed_motion') && ref?.startsWith('carry:')) return 'Carried to this meeting'
  return AUTO_SOURCE_LABELS[source]
}

// Financial reports are never consent items (owner, 2026-10-26): the board
// takes up the Treasurer's report on its own. An item is one when its type
// is the financial report, or its title plainly is (read the way the
// resolution book reads routine-approval titles).
export function isFinancialReportItem(item: { itemType?: string | null; type?: string | null; title: string }): boolean {
  return (item.itemType ?? item.type) === 'financial_report' || isFinancialReportTitle(item.title)
}
// Null when the item may go on the consent agenda; otherwise why not, in words.
export function consentProblem(item: { itemType?: string | null; type?: string | null; title: string }): string | null {
  if (!isFinancialReportItem(item)) return null
  return `“${item.title}” is a financial report, and financial reports are never on the consent agenda. Put it on the regular agenda so the board takes it up on its own.`
}

// A director's request that a consent item be discussed separately
// (owner, 2026-10-26), as staff see it on the meeting page and conductor.
export type ConsentRequestView = { id: string; person: { id: string; name: string }; requestedAt: string }

// ----------------------------------------------------------- templates

export const PlannedMotionStubSchema = z.object({
  motionType: z.string().regex(/^[a-z_]{2,60}$/),
  title: z.string().trim().min(1).max(300),
  text: z.string().trim().min(1).max(20000),
})
const TemplateItemBase = z.object({
  type: z.enum(AGENDA_ITEM_TYPES),
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().max(4000).nullable().optional(),
  presenterName: z.string().trim().max(160).nullable().optional(),
  durationMinutes: z.number().int().min(1).max(600).nullable().optional(),
  executive: z.boolean().optional(),
  motions: z.array(PlannedMotionStubSchema).max(10).optional(),
})
// A template item and its sub-items, as deep as the rules allow (ADR 0062).
export type AgendaTemplateItem = z.infer<typeof TemplateItemBase> & { children?: AgendaTemplateItem[] | undefined }
export const AgendaTemplateItemSchema: z.ZodType<AgendaTemplateItem> = z.lazy(() => TemplateItemBase.extend({ children: z.array(AgendaTemplateItemSchema).max(40).optional() }))

export type FlatTemplateItem = { item: AgendaTemplateItem; id: string; parentId: string | null; depth: number; parent: AgendaTemplateItem | null }

// Every item of a template tree with its depth and parent, in reading order.
export function flattenTemplate(items: AgendaTemplateItem[]): FlatTemplateItem[] {
  const out: FlatTemplateItem[] = []
  const walk = (list: AgendaTemplateItem[], parentId: string | null, parent: AgendaTemplateItem | null, depth: number) =>
    list.forEach((item, k) => {
      const id = parentId === null ? `t${k}` : `${parentId}.${k}`
      out.push({ item, id, parentId, depth, parent })
      walk(item.children ?? [], id, item, depth + 1)
    })
  walk(items, null, null, 0)
  return out
}

// A template tree with each item changed by f, top down (null drops the
// item and what is under it). An item left without sub-items has no
// children key.
export function mapTemplate(items: AgendaTemplateItem[], f: (item: AgendaTemplateItem, parent: AgendaTemplateItem | null) => AgendaTemplateItem | null, parent: AgendaTemplateItem | null = null): AgendaTemplateItem[] {
  const out: AgendaTemplateItem[] = []
  for (const i of items) {
    const mapped = f(i, parent)
    if (!mapped) continue
    const { children, ...rest } = mapped
    const kept = children ? mapTemplate(children, f, mapped) : []
    out.push(kept.length > 0 ? { ...rest, children: kept } : rest)
  }
  return out
}

// What is wrong with a template tree's shape, in words.
export function templateNestingProblems(items: AgendaTemplateItem[]): string[] {
  const problems: string[] = []
  const flat = flattenTemplate(items)
  if (flat.length > 300) problems.push('A template holds 300 items at most')
  for (const { item, depth, parent } of flat) {
    if (depth > AGENDA_MAX_DEPTH) problems.push(`Sub-items go ${AGENDA_MAX_DEPTH} levels deep at most (“${item.title}”)`)
    if (depth > 0 && isAgendaGroup(item.type)) problems.push('The consent agenda, old business and new business stay on the top level')
    if (parent?.type === 'consent_agenda') {
      if (item.executive) problems.push('Nothing from the executive session goes on the consent agenda')
      if ((item.motions?.length ?? 0) > 0) problems.push('Consent items are adopted by the consent agenda’s single motion; they carry no motion of their own')
      if ((item.children?.length ?? 0) > 0) problems.push('Consent agenda items have no sub-items')
    }
  }
  return [...new Set(problems)]
}

export const AgendaTemplateItemsSchema = z
  .array(AgendaTemplateItemSchema)
  .min(1)
  .max(80)
  .superRefine((items, ctx) => {
    for (const message of templateNestingProblems(items)) ctx.addIssue({ code: 'custom', message })
  })

export type AgendaTemplateView = {
  id: string
  name: string
  description: string | null
  body: MeetingBody
  committeeId: string | null
  committeeName: string | null
  meetingKind: MeetingKind | null
  seriesId: string | null
  seriesName: string | null
  items: AgendaTemplateItem[]
  totalMinutes: number
  version: number
  retired: boolean
  updatedAt: string
  // A financial report under its consent agenda (saved before 2026-10-26):
  // the template can't be applied until it is edited.
  consentProblem?: string | null
}

// Who an action item from a template goes to: an office (whoever holds it
// on the meeting date), the Executive Director, a named person, or nobody.
export const ACTION_ASSIGNEES = ['executive_director', 'president', 'vice_president', 'secretary', 'treasurer', 'past_president'] as const
export const ActionTemplateItemSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().trim().max(4000).nullable().optional(),
  assignee: z.union([z.enum(ACTION_ASSIGNEES), z.string().regex(/^person:[A-Za-z0-9_-]{1,100}$/)]).nullable().optional(),
  dueDaysAfter: z.number().int().min(0).max(365).nullable().optional(),
})
export const ActionTemplateItemsSchema = z.array(ActionTemplateItemSchema).min(1).max(50)
export type ActionTemplateItem = z.infer<typeof ActionTemplateItemSchema>
export type ActionTemplateView = { id: string; name: string; description: string | null; body: MeetingBody; meetingKind: MeetingKind | null; items: ActionTemplateItem[]; version: number; retired: boolean; updatedAt: string }

// Does a template fit a meeting? Its body always; its committee and kind
// when it names them.
export function templateFits(t: { body: MeetingBody; committeeId: string | null; meetingKind: MeetingKind | null }, m: { body: MeetingBody; committeeId: string | null; kind: MeetingKind }): string | null {
  if (t.body !== m.body) return `This template is for ${t.body === 'board' ? 'board' : t.body === 'executive' ? 'Executive Committee' : 'committee'} meetings`
  if (t.committeeId && t.committeeId !== m.committeeId) return 'This template is for another committee'
  if (t.meetingKind && t.meetingKind !== m.kind) return `This template is for ${t.meetingKind} meetings`
  return null
}

// ------------------------------------------------------------- agenda time

// The minutes an agenda is expected to take: each top-level item's own
// minutes, or, for one without its own, its sub-items' (each of those its
// own, or its sub-items' sum, and so on down).
export function agendaTotalMinutes(items: Timed[]): number {
  return agendaOutline(items)
    .filter((e) => e.depth === 0)
    .reduce((n, e) => n + agendaItemMinutes(items, e.item.id), 0)
}

const clock = (minutesOfDay: number) => {
  const m = ((minutesOfDay % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

// Each item's planned start (HH:MM, Burlington time) from the meeting's
// start, in agenda order (each item followed by its sub-items, which start
// with it and run one after another), and the planned end.
export function agendaSchedule(startsLocal: string, items: Timed[]): { starts: Record<string, string>; endsAt: string; totalMinutes: number } {
  const [h, mi] = startsLocal.slice(11, 16).split(':').map(Number) as [number, number]
  const starts: Record<string, string> = {}
  const outline = agendaOutline(items)
  const place = (parentId: string | null, from: number): number => {
    let at = from
    for (const e of outline.filter((x) => x.parentId === parentId)) {
      starts[e.item.id] = clock(at)
      const inner = place(e.item.id, at)
      at += e.item.durationMinutes ?? inner - at
    }
    return at
  }
  const end = place(null, h * 60 + mi)
  return { starts, endsAt: clock(end), totalMinutes: agendaTotalMinutes(items) }
}

export const agendaMinutesLabel = (n: number) => (n >= 60 ? `${Math.floor(n / 60)} h${n % 60 ? ` ${n % 60} min` : ''}` : `${n} min`)

// ------------------------------------------------------------------ timers

// Seconds since the call to order, frozen at adjournment.
export function meetingElapsedSeconds(calledToOrderAt: string | null, adjournedAt: string | null, nowMs: number): number {
  if (!calledToOrderAt) return 0
  const end = adjournedAt ? Date.parse(adjournedAt) : nowMs
  return Math.max(0, Math.floor((end - Date.parse(calledToOrderAt)) / 1000))
}

export type TimerState = 'untimed' | 'ok' | 'warning' | 'over'

// An item's time so far (every segment it was taken up), against its
// allotted minutes: a warning in the last fifth (at least the last minute),
// over once past.
export function itemTimer(segments: { startedAt: string; endedAt: string | null }[], allottedMinutes: number | null, nowMs: number): { elapsedSeconds: number; remainingSeconds: number | null; state: TimerState; running: boolean } {
  let elapsed = 0
  let running = false
  for (const s of segments) {
    const end = s.endedAt ? Date.parse(s.endedAt) : nowMs
    if (!s.endedAt) running = true
    elapsed += Math.max(0, Math.floor((end - Date.parse(s.startedAt)) / 1000))
  }
  if (!allottedMinutes) return { elapsedSeconds: elapsed, remainingSeconds: null, state: 'untimed', running }
  const allotted = allottedMinutes * 60
  const remaining = allotted - elapsed
  const warnAt = Math.max(60, Math.floor(allotted / 5))
  return { elapsedSeconds: elapsed, remainingSeconds: remaining, state: remaining < 0 ? 'over' : remaining <= warnAt ? 'warning' : 'ok', running }
}

// 75 → "1:15"; 3725 → "1:02:05"; negative values keep their sign.
export function formatClock(seconds: number): string {
  const sign = seconds < 0 ? '-' : ''
  const s = Math.abs(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${sign}${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${sign}${m}:${String(sec).padStart(2, '0')}`
}

// ---------------------------------------------------------- consent agenda

export const CONSENT_MOTION_TITLE = 'Adopt the consent agenda'

// Robert's Rules: the consent agenda is adopted by a single motion, whose
// text names every item on it at the moment the vote opens.
export function consentMotionText(items: { title: string }[], body: 'board' | 'executive' | 'committee' = 'board'): string {
  const who = body === 'executive' ? 'the Executive Committee' : body === 'committee' ? 'the committee' : 'the board'
  if (items.length === 0) return `Moved, that ${who} adopts the consent agenda. (The consent agenda has no items.)`
  return `Moved, that ${who} adopts the consent agenda, consisting of: ${items.map((i, n) => `(${n + 1}) ${i.title.replace(/\.$/, '')}`).join('; ')}.`
}

// ------------------------------------- lay on the table, postpone (Robert's)

export const DISPOSITIONS = ['tabled', 'postponed'] as const
export type Disposition = (typeof DISPOSITIONS)[number]
export const DISPOSITION_REASONS: Record<Disposition, string> = { tabled: 'Laid on the table', postponed: 'Postponed to the next meeting' }
export const DISPOSITION_TITLES: Record<Disposition, string> = { tabled: 'Lay on the table', postponed: 'Postpone to the next meeting' }

// ------------------------------------------------------------ the minutes

// A section the ED adds has a key starting "custom_"; every other key is a
// section Hub generates from the record, which stays regenerable and comes
// out of a draft only with a note.
export const CUSTOM_SECTION_PREFIX = 'custom_'
export const isGeneratedSection = (key: string) => !key.startsWith(CUSTOM_SECTION_PREFIX)
export type MinutesRemoval = { key: string; title: string; note: string }

// What a draft save would break: a generated section removed without a
// note. (Executive sections a reader can't see are kept by core as they
// were and are not passed here.)
export function minutesEditProblems(current: MinutesSection[], next: MinutesSection[], removals: { key: string; note: string }[]): string[] {
  const problems: string[] = []
  const keys = new Set(next.map((s) => s.key))
  if (keys.size !== next.length) problems.push('Each section needs its own key')
  for (const s of current) {
    if (keys.has(s.key) || !isGeneratedSection(s.key)) continue
    const r = removals.find((x) => x.key === s.key)
    if (!r || r.note.trim().length === 0) problems.push(`“${s.title}” is generated from the meeting's record; say why it comes out of the minutes`)
  }
  for (const s of next) if (!current.some((c) => c.key === s.key) && isGeneratedSection(s.key)) problems.push(`A new section's key starts with “${CUSTOM_SECTION_PREFIX}”`)
  return problems
}

// ------------------------------------------------------------- reminders

// Which packet reminder is due today (the number of days before, from the
// practice setting), or null: the tightest reminder whose window has
// opened, never on the day the packet was published (its "packet is ready"
// email went that day) and never after the meeting. The outbox's dedupe
// key sends each one once.
export function packetReminderDue(input: { today: string; meetingDay: string; publishedDay: string; days: number[] }): number | null {
  const until = daysBetween(input.today, input.meetingDay)
  if (until < 0 || input.publishedDay >= input.today) return null
  const windows = [...input.days].filter((d) => d >= until).sort((a, b) => a - b)
  return windows[0] ?? null
}

// agendaApprovalItem (2026-10-26): Hub puts "Approval of the agenda" on the
// consent agenda of regular and annual board meetings (on by default).
export type GovernancePractice = { packetReminderDays: number[]; presidentSignature: 'optional' | 'required'; agendaApprovalItem: boolean }
export const DEFAULT_GOVERNANCE_PRACTICE: GovernancePractice = { packetReminderDays: [3, 1], presidentSignature: 'optional', agendaApprovalItem: true }

// ------------------------------------------- board notifications (ADR 0052)

// On or Paused. While paused every email to a director is written to the
// governance outbox as 'held' and never sent, and emailed meeting notice is
// refused; staff notifications in Hub carry on. Turning them back on sends
// nothing held unless staff pick it.
export const BOARD_NOTIFICATION_STATES = ['on', 'paused'] as const
export type BoardNotificationState = (typeof BOARD_NOTIFICATION_STATES)[number]
export const GOVERNANCE_EMAIL_KIND_LABELS: Record<string, string> = {
  vote_open: 'Vote open',
  packet_ready: 'Packet ready',
  packet_reminder: 'Packet reminder',
  email_meeting_to_send: 'Email meeting to send',
  minutes_to_sign: 'Minutes to sign',
  minutes_review: 'Draft minutes to review',
  special_meeting: 'Special meeting call',
  resignation: 'Resignation or absence',
  task_force: 'Task force approval',
  assessment: 'Self-assessment',
  election: 'Election',
}
export type BoardNotificationsView = {
  state: BoardNotificationState
  changedAt: string | null
  changedBy: string | null
  held: { total: number; byKind: { kind: string; label: string; count: number }[] }
}
// A held email, for the resume dialog. `sendable` is false once what it is
// about has passed (a meeting that has started, a vote no longer open).
export type HeldBoardEmailView = { id: string; kind: string; label: string; to: string; subject: string; createdAt: string; sendable: boolean; blockedReason: string | null; meeting: { id: string; title: string; startsLocal: string } | null }

export type MinutesCountersignatureView ={ role: 'president'; person: PersonRef; signedAt: string; channel: 'mybre' | 'attested'; attestation: string | null; hash: string }

// ------------------------------------------------------------ the board book

export const BOARD_BOOK_VARIANTS = ['full', 'redacted'] as const
export type BoardBookVariant = (typeof BOARD_BOOK_VARIANTS)[number]
export const boardBookKey = (packetId: string, variant: BoardBookVariant, contentsHash: string) => `board-books/${packetId}/${variant}-${contentsHash}.pdf`

// What the web Worker compiles: the packet version's frozen contents (with
// the executive session already redacted for the 'redacted' variant) and
// each frozen document version it merges.
export type BoardBookManifest = {
  packetId: string
  variant: BoardBookVariant
  version: number
  hash: string
  publishedAt: string
  contents: BoardBookContents
  documents: { documentId: string; version: number | null; title: string; kind: string; r2Key: string | null; sha256: string | null; contentType: string | null; itemTitle: string; url?: string | null }[]
  existing: { r2Key: string; sha256: string; pageCount: number } | null
}

export type BoardBookMotion = { id: string; typeLabel: string; title: string; text: string; threshold: string | null; subjectName?: string | null; label?: string }

export type BoardBookItem = {
  id: string
  position: number
  title: string
  itemType?: AgendaItemType
  parentId?: string | null
  description: string | null
  presenterName: string | null
  durationMinutes: number | null
  executive: boolean
  redacted?: boolean
  autoSource?: AutoSource | null
  // ADR 0065: a carried item's label (only when there is one).
  label?: string
  documents: { documentId: string; title: string; kind: string; version: number | null; sha256: string | null }[]
  proposedMotions?: BoardBookMotion[]
  // ADR 0067: on the item that asks the board to ratify an Executive
  // Committee action (only there): the resolution as adopted and the
  // documents on the committee's agenda item at the version the committee
  // saw. The board book carries both; nothing else lists them. A reader
  // without the executive session gets the number only of an
  // executive-session action.
  ratification?: BoardBookRatification
  // ADR 0069: on the item that brings the board an Executive Committee
  // recommendation (only there): the EC- resolution as adopted and the
  // documents on the committee's agenda item at the version the committee
  // saw, in the same shape.
  recommendation?: BoardBookRatification
  // ADR 0068: on the item whose proposed motion acts on a version of a
  // governing document (only there): its text and redline as they stood.
  manual?: BoardBookManual
}

export type BoardBookRatification = {
  resolutionId: string
  number: string
  executive: boolean
  redacted?: boolean
  record?: { title: string; text: string; adoptedOn: string; meetingTitle: string | null; mover: string | null; seconder: string | null; tally: { yea: number; nay: number; abstain: number; recused?: number } | null }
  documents?: { documentId: string; title: string; kind: string; version: number | null; sha256: string | null }[]
}

export type BoardBookContents = {
  meeting: { id: string; title: string; body: MeetingBody; kind: MeetingKind; startsLocal: string; location: string | null }
  items: BoardBookItem[]
  // The elections at the meeting (ADR 0030) and, frozen when the slate was
  // submitted, the Report of the Nominating Committee for the May meeting
  // it is presented at and the Annual Meeting (ADR 0060).
  elections?: { id: string; title: string; presented?: boolean; report?: SlateReport; slots?: { label: string; termStartsOn: string; termEndsOn: string; threshold: string; nominee: string | null; conditional: string | null }[] }[]
  // ADR 0083: proposed motions under no agenda item (only when there are any).
  otherProposedMotions?: BoardBookMotion[]
  openActionItems?: { title: string; assigneeName: string | null; dueOn: string | null; meetingTitle: string; status: string }[]
  priorMinutes?: { minutesId: string; meetingTitle: string; meetingStartsLocal: string; version: number; hash: string; sections: { title: string; body: string; executive: boolean }[] }[]
}

// ---------------------------------------------- an item's documents (ADR 0051)

// One document that could go on an agenda item: one already filed on the
// meeting (attached as it is) or one from elsewhere in Hub (copied in).
export type AgendaDocumentChoice = {
  documentId: string
  title: string
  classification: string
  kind: 'file' | 'link'
  contentType: string | null
  sizeBytes: number | null
  createdAt: string
  onThisMeeting: boolean
  // Where it is filed, in words: "This meeting", "Grant", a meeting's title.
  from: string
  attached: boolean
  // Why it can't go on this item, if it can't.
  blocked: string | null
  // A confidential document on an item outside the executive session.
  needsConfirmation: boolean
  // The classification it would have on the item.
  attachedAs: string
}

// An item's documents as the meeting screen and the presentation view show
// them: the version in the latest packet ("packet") when one is published
// with the item, anything attached since at its current version ("live").
export type AgendaPresentationDocument = {
  documentId: string
  title: string
  classification: string
  kind: 'file' | 'link'
  url: string | null
  version: number | null
  contentType: string | null
  sizeBytes: number | null
  filename: string | null
  source: 'packet' | 'live'
  // In the packet, but taken off the item (or retired) since.
  removedSincePacket: boolean
}

export type AgendaPresentation = {
  item: { id: string; title: string; executive: boolean }
  packet: { id: string; version: number; publishedAt: string } | null
  documents: AgendaPresentationDocument[]
}

// How a document opens on the meeting screen: a PDF in the browser's own
// viewer, an image as it is, anything else by opening or downloading it.
export function documentViewKind(d: { kind: 'file' | 'link'; contentType: string | null }): 'pdf' | 'image' | 'link' | 'other' {
  if (d.kind === 'link') return 'link'
  if (d.contentType === 'application/pdf') return 'pdf'
  if (d.contentType && /^image\/(png|jpeg|webp|gif)$/.test(d.contentType)) return 'image'
  return 'other'
}

// ADR 0083: what the board book can print in full.
export const printsInBoardBook = (d: { kind: 'file' | 'link'; contentType: string | null }) => d.kind === 'file' && ['application/pdf', 'image/png', 'image/jpeg', 'text/plain', 'text/csv'].includes(d.contentType ?? '')

// "PDF", "Word", "Image": a document's type in a word or two.
export function documentTypeLabel(d: { kind: 'file' | 'link'; contentType: string | null }): string {
  if (d.kind === 'link') return 'Link'
  const t = d.contentType ?? ''
  if (t === 'application/pdf') return 'PDF'
  if (t.startsWith('image/')) return 'Image'
  if (/word|officedocument\.wordprocessing/.test(t)) return 'Word'
  if (/excel|spreadsheet/.test(t)) return 'Excel'
  if (/powerpoint|presentation/.test(t)) return 'PowerPoint'
  if (t === 'text/csv') return 'CSV'
  if (t.startsWith('text/')) return 'Text'
  return 'File'
}

export function fileSizeLabel(bytes: number | null): string | null {
  if (bytes === null || bytes === undefined) return null
  if (bytes < 1024) return `${bytes} bytes`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
